/* eslint-disable import/no-named-as-default */
import { Form, Formik } from 'formik';
import { useParams } from 'react-router-dom';
import Select, { CSSObjectWithLabel } from 'react-select';

import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import TopLabel from '@/components/common/TopLabel/TopLabel';
import { Input } from '@/components/common/InputTag4/InputTag4';
import ValidationAlert from '@/components/common/ValidationAlert/ValidationAlert';
import { validationSchema } from './validationSchema';
import { Checkbox, CheckboxChange } from '@/components/common/CheckboxTag4/CheckboxTag4';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import styles from './edit-bundle.module.scss';
import useUpdateBundle from '../../hooks/useUpdateBundle';
import useGetContractDetails from '@/hooks/contract/useGetContractDetails.hook';
import Spinner from '@/components/common/Spinner/Spinner';
import { softwareCompanies } from '@/utils/data-helpers/softwareCompanies';
import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';
import { BundleTypeEnum } from '@/graphql/contracts/_generated_/contracts';

const bundleTypes = [
  { value: 'Contractual', label: 'Contractual' },
  { value: 'Custom', label: 'Custom' },
  { value: 'Suite', label: 'Suite' },
];

interface IBundleFormValues {
  bundleId: string;
  description: string;
  hideInvoiceLines: boolean;
  hideInvoiceValues: boolean;
  softwareCompany: string | undefined;
  type: BundleTypeEnum | undefined;
}

interface IEditBundleProps {
  onClose: () => void;
  bundle: IBundleFormValues
}

export default function EditBundleModal({ onClose, bundle }: IEditBundleProps) {
  const { updateBundle } = useUpdateBundle();
  const { contractId }: { contractId: UUID } = useParams();
  const { isContractLoading } = useGetContractDetails(contractId);

  const handleSubmit = (values: IBundleFormValues) => {
    updateBundle(values);
    onClose();
  };

  const customFontStyle = (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...base,
    fontSize: '13px',
  } as CSSObjectWithLabel);

  return (
    <AgModalWindow
      title={'Edit Bundle'}
      isOpen={true}
      onClose={onClose}
      contentStyle={{ height: '500px' }}
    >
      {isContractLoading &&
        <div className="d-flex justify-content-center align-items-center" style={{ height: 200 }}>
          <Spinner />
        </div>
      }

      {!isContractLoading &&
        <Formik<IBundleFormValues>
          initialValues={{
            bundleId: bundle.bundleId,
            description: bundle.description,
            hideInvoiceLines: bundle.hideInvoiceLines ?? false,
            hideInvoiceValues: bundle.hideInvoiceValues ?? false,
            softwareCompany: bundle.softwareCompany ?? undefined,
            type: bundle.type,
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => handleSubmit(values)}
          validateOnMount={true}
        >
          {({ values, setFieldValue, isValid, errors, touched }) => (
            <Form>
              <div className="mb-4">
                <TopLabel id="description" label="Description" required={false} withMargin={true} />
                <Input
                  id="description"
                  placeholder="Enter description"
                  value={values.description}
                  onChange={(e) => setFieldValue('description', e.target.value)}
                  className={styles['description-input']}
                />
                {errors.softwareCompany && touched.softwareCompany && (
                  <ValidationAlert isFloating={true} type="danger">
                    {errors.softwareCompany}
                  </ValidationAlert>
                )}
              </div>

              <div style={{ height: '100px' }}>
                <TopLabel id="softwareCompany" label="Software Company" required={false} withMargin={true} />
                <div >
                  <Select
                    id="softwareCompany"
                    options={softwareCompanies}
                    value={softwareCompanies.find(option => option.value === values.softwareCompany) || null}
                    onChange={(selectedOption) => {
                      setFieldValue('softwareCompany', selectedOption?.value);
                    }}
                    placeholder="Select software company..."
                    isClearable
                    maxMenuHeight={120}
                    styles={{
                      control: (base) => customFontStyle(base),
                      placeholder: (base) => customFontStyle(base),
                      singleValue: (base) => customFontStyle(base),
                      menu: (base) => customFontStyle(base)
                    }}
                  />
                </div>
                {errors.softwareCompany && touched.softwareCompany && (
                  <ValidationAlert isFloating={true} type="danger">
                    {errors.softwareCompany}
                  </ValidationAlert>
                )}
              </div>

              <div style={{ height: '100px' }}>
                <TopLabel id="type" label="Type" required={false} withMargin={true} />
                <div >
                  <Select
                    id="type"
                    options={bundleTypes}
                    value={bundleTypes.find(option => option.value === values.type) || null}
                    onChange={(selectedOption) => {
                      setFieldValue('type', selectedOption?.value);
                    }}
                    placeholder="Select bundle type..."
                    isClearable
                    maxMenuHeight={120}
                    styles={{
                      control: (base) => customFontStyle(base),
                      placeholder: (base) => customFontStyle(base),
                      singleValue: (base) => customFontStyle(base),
                      menu: (base) => customFontStyle(base)
                    }}
                  />
                </div>
                {errors.type && touched.type && (
                  <ValidationAlert isFloating={true} type="danger">
                    {errors.type}
                  </ValidationAlert>
                )}
              </div>

              <div className="d-flex" >
                <div className="col-4 mb-3">
                  <Checkbox
                    id="hideInvoiceLines"
                    checked={values.hideInvoiceLines}
                    onChange={(e: CheckboxChange) =>
                      setFieldValue('hideInvoiceLines', e.checked)
                    }
                  >
                    Hide Invoice Lines
                  </Checkbox>
                </div>
                <div className="col-4 mb-3">
                  <Checkbox
                    title='Hide Invoice Values'
                    id="hideInvoiceValues"
                    checked={values.hideInvoiceValues}
                    onChange={(e: CheckboxChange) =>
                      setFieldValue('hideInvoiceValues', e.checked)
                    }>
                    Hide Invoice Values
                  </Checkbox>
                </div>
              </div>

              <ActionButtonsWrapper
                leftButton={
                  <Button
                    buttonStyleType={ButtonStyleType.PRIMARY}
                    onClick={onClose}>
                    Cancel
                  </Button>
                }
                rightButton={
                  <Button
                    buttonStyleType={ButtonStyleType.PRIMARY}
                    type="submit"
                    disabled={!isValid}>
                    Save
                  </Button>
                }
              />
            </Form>
          )}
        </Formik>
      }
    </AgModalWindow >
  );
}
